import moment from 'moment';
import 'moment-timezone';
import {
  DRIVER_ACTION_EVENT_TYPES,
  fetchDriverActionEvents,
} from './driverActions';
import { GetSupervisorAction } from './GraphQL';

// A supervisor dispatch-coding a stop writes a DispatchUnreachable
// SupervisorAction, not a DriverAction; the status ladder reads both.
export const DISPATCH_UNREACHABLE_EVENT_TYPE = 'DispatchUnreachable';
export const DISPATCH_SOURCE = 'CMS_DISPATCH';

// Driver-action stop-status derivation. Extracted from ViewStockingDashboard
// so the Supervisor Dashboard's Route Operations tab can surface the same
// statuses. VSD still keeps its own copy of the ladder in
// `extendRoutesWithAdditionalData`; a rule change here must be made there too.
// The full precedence ladder is documented on `deriveStopStatus` below.

export const STATUS_IDS = {
  NOT_STARTED: 'NOT_STARTED',
  VAN_TEMPERATURE_SET: 'VAN_TEMPERATURE_SET',
  IN_PROGRESS: 'IN_PROGRESS',
  INCOMPLETE: 'INCOMPLETE',
  COMPLETED: 'COMPLETED',
  UNREACHABLE_INCOMPLETE: 'UNREACHABLE_INCOMPLETE',
  UNREACHABLE_COMPLETE: 'UNREACHABLE_COMPLETE',
  CLEAROUT_COMPLETE: 'CLEAROUT_COMPLETE',
  CLEAROUT_DECOMMISSION_COMPLETE: 'CLEAROUT_DECOMMISSION_COMPLETE',
  // Supervisor-staged: a missed stop from a previous day that the supervisor
  // has queued onto today's route via the Missed Stops Review section.
  // NextMV assigns the actual sequence position when the route re-optimizes;
  // until then the row appears at the bottom with a "?" placeholder badge.
  MISSED_STOP_PENDING: 'MISSED_STOP_PENDING',
};

// `tone` is one of 'on-track' | 'in-progress' | 'off-track' | 'neutral'.
// Consumers map tones to theme tokens via `theme.statusPillTones` — see the
// StopStatusPill atom.
export const STATUS_META = {
  [STATUS_IDS.NOT_STARTED]: {
    label: 'Not started',
    tone: 'neutral',
    description: 'Driver has not engaged with this stop yet.',
  },
  [STATUS_IDS.VAN_TEMPERATURE_SET]: {
    label: 'Van temp set',
    tone: 'in-progress',
    description: 'Van temperature logged, packing slip not yet scanned.',
  },
  [STATUS_IDS.IN_PROGRESS]: {
    label: 'In progress',
    tone: 'in-progress',
    description:
      'Driver scanned the packing slip but has not finished stocking.',
  },
  [STATUS_IDS.INCOMPLETE]: {
    label: 'Incomplete',
    tone: 'off-track',
    description:
      'Driver scanned this stop, did not finish stocking, and has already scanned a later stop.',
  },
  [STATUS_IDS.COMPLETED]: {
    label: 'Completed',
    tone: 'on-track',
    description: 'Stop was scanned and stocked successfully.',
  },
  [STATUS_IDS.UNREACHABLE_INCOMPLETE]: {
    label: 'Unreachable',
    tone: 'off-track',
    description:
      'Marked unreachable by the driver or from Dispatch; no packing slip scan recorded.',
  },
  [STATUS_IDS.UNREACHABLE_COMPLETE]: {
    label: 'Unreachable (scanned)',
    tone: 'on-track',
    description:
      'Marked unreachable by the driver or from Dispatch after the packing slip was scanned.',
  },
  [STATUS_IDS.CLEAROUT_COMPLETE]: {
    label: 'Removal complete',
    tone: 'on-track',
    description: 'Inventory removal location has been completed.',
  },
  [STATUS_IDS.CLEAROUT_DECOMMISSION_COMPLETE]: {
    label: 'Decommission complete',
    tone: 'on-track',
    description: 'Clearout decommission location has been completed.',
  },
  [STATUS_IDS.MISSED_STOP_PENDING]: {
    label: 'Missed stop · pending insert',
    tone: 'in-progress',
    description:
      "Staged from yesterday's Missed Stops Review. NextMV will assign the sequence position when the route re-optimizes.",
  },
};

// Fan out the driver-action event types for `stockingDate` (the shared pipeline
// covers the previous day and the stocking-window filter) and bucket them by
// family for `deriveStopStatus`. `failedEventTypes` lists any type that came
// back incomplete so callers can flag partial data.
export async function fetchDispatchUnreachable(stockingDate) {
  try {
    return await GetSupervisorAction({
      reviewDate: stockingDate,
      eventType: DISPATCH_UNREACHABLE_EVENT_TYPE,
      allUsers: true,
    });
  } catch (err) {
    console.error(
      `[stopStatus] DispatchUnreachable fetch failed for ${stockingDate}:`,
      err,
    );
    return null;
  }
}

export async function fetchDriverActionsForDate(stockingDate) {
  const [{ eventsByType, failedEventTypes }, dispatchUnreachableItems] =
    await Promise.all([
      fetchDriverActionEvents(DRIVER_ACTION_EVENT_TYPES, stockingDate),
      fetchDispatchUnreachable(stockingDate),
    ]);

  return {
    stockedLocationsItems: [
      ...eventsByType.LocationStocked,
      ...eventsByType.DropOffComplete,
    ],
    packingSlipQueriedItems: eventsByType.PackingSlipQuery,
    locationUnreachableItems: eventsByType.LocationUnreachable,
    vanTemperatureItems: eventsByType.VanTemperature,
    inventoryRemovalItems: eventsByType.InventoryRemoval,
    clearOutDecommissionItems: eventsByType.ClearOutDecommission,
    dispatchUnreachableItems: dispatchUnreachableItems || [],
    failedEventTypes: dispatchUnreachableItems
      ? failedEventTypes
      : [...failedEventTypes, DISPATCH_UNREACHABLE_EVENT_TYPE],
  };
}

function safeParse(data) {
  if (!data || typeof data !== 'string') return null;
  try {
    return JSON.parse(data);
  } catch (err) {
    return null;
  }
}

function formatChicago(ts) {
  if (!ts) return '';
  return moment.unix(ts).tz('America/Chicago').format('ddd h:mm A');
}

// Derive `{ status, stockedAt, scannedAt, driver }` for a single LocationRoute
// row given the bucketed events. Mirrors the precedence ladder VSD uses in
// `extendRoutesWithAdditionalData`:
//
//   default        → NOT_STARTED
//   VanTemperature → VAN_TEMPERATURE_SET
//   PackingSlipQuery → IN_PROGRESS, or INCOMPLETE if a same-driver
//                      PackingSlipQuery exists later for any location
//   LocationStocked / DropOffComplete → COMPLETED (overrides earlier)
//   LocationUnreachable (and not stocked) →
//     UNREACHABLE_COMPLETE if a PackingSlipQuery also exists,
//     UNREACHABLE_INCOMPLETE otherwise
//   InventoryRemoval → CLEAROUT_COMPLETE
//   ClearOutDecommission → CLEAROUT_DECOMMISSION_COMPLETE
export function deriveStopStatus({ stop, eventBuckets }) {
  if (!stop || !eventBuckets) {
    return { status: STATUS_IDS.NOT_STARTED };
  }
  const {
    stockedLocationsItems = [],
    packingSlipQueriedItems = [],
    locationUnreachableItems = [],
    vanTemperatureItems = [],
    inventoryRemovalItems = [],
    clearOutDecommissionItems = [],
    dispatchUnreachableItems = [],
  } = eventBuckets;

  const locationId = stop.id;
  let status = STATUS_IDS.NOT_STARTED;
  let stockedAt = '';
  let scannedAt = '';
  let driver = '';
  // `{ reason, subReason, source }` from the LocationUnreachable payload, or
  // `{ reason, subReason, source, notes, createdAt }` from a DispatchUnreachable
  // supervisor action; null unless the stop is unreachable.
  let unreachable = null;

  if (vanTemperatureItems.some((it) => it.locationId === locationId)) {
    status = STATUS_IDS.VAN_TEMPERATURE_SET;
  }

  const queriedItem = packingSlipQueriedItems.find(
    (it) => it.locationId === locationId,
  );
  if (queriedItem) {
    scannedAt = formatChicago(queriedItem._lastModified);
    const parsed = safeParse(queriedItem.data);
    if (parsed?.driver) driver = parsed.driver.split('@')[0];
    status = STATUS_IDS.IN_PROGRESS;

    // If a later PackingSlipQuery exists for the same driver (anywhere),
    // the driver moved on without stocking → INCOMPLETE.
    const queriedDriver = parsed?.driver || '';
    const hasLaterScan = packingSlipQueriedItems.some((other) => {
      if (other === queriedItem) return false;
      if (!other?._lastModified) return false;
      if (other._lastModified <= queriedItem._lastModified) return false;
      const otherParsed = safeParse(other.data);
      return (otherParsed?.driver || '') === queriedDriver;
    });
    if (hasLaterScan) status = STATUS_IDS.INCOMPLETE;
  }

  const stockedItem = stockedLocationsItems.find(
    (it) => it.locationId === locationId,
  );
  if (stockedItem) {
    status = STATUS_IDS.COMPLETED;
    stockedAt = formatChicago(stockedItem._lastModified);
  }

  if (!stockedItem) {
    const unreachableItem = locationUnreachableItems.find(
      (it) => it.locationId === locationId,
    );
    if (unreachableItem) {
      const parsed = safeParse(unreachableItem.data);
      if (parsed?.driver) driver = parsed.driver.split('@')[0];
      unreachable = {
        reason: parsed?.reason || '',
        subReason: parsed?.subReason || '',
        source: parsed?.source || '',
      };
    } else {
      // Rows arrive newest-first, so the first match is the latest coding.
      const dispatchItem = dispatchUnreachableItems.find(
        (it) => it?.data?.locationId === locationId,
      );
      if (dispatchItem) {
        const { data } = dispatchItem;
        if (data.supervisorEmail) driver = data.supervisorEmail.split('@')[0];
        unreachable = {
          reason: data.reason || '',
          subReason: data.subReason || '',
          source: DISPATCH_SOURCE,
          notes: data.notes || '',
          createdAt: dispatchItem.createdAt || '',
        };
      }
    }
    if (unreachable) {
      const wasScanned = packingSlipQueriedItems.some(
        (it) => it.locationId === locationId,
      );
      status = wasScanned
        ? STATUS_IDS.UNREACHABLE_COMPLETE
        : STATUS_IDS.UNREACHABLE_INCOMPLETE;
    }
  }

  const removalItem = inventoryRemovalItems.find(
    (it) => it.locationId === locationId,
  );
  if (removalItem) {
    const parsed = safeParse(removalItem.data);
    if (parsed?.contactName) driver = parsed.contactName.split('@')[0];
    status = STATUS_IDS.CLEAROUT_COMPLETE;
  }

  const decommItem = clearOutDecommissionItems.find(
    (it) => it.locationId === locationId,
  );
  if (decommItem) {
    const parsed = safeParse(decommItem.data);
    if (parsed?.contactName) driver = parsed.contactName.split('@')[0];
    status = STATUS_IDS.CLEAROUT_DECOMMISSION_COMPLETE;
  }

  return { status, stockedAt, scannedAt, driver, unreachable };
}

// Convenience: bucket-driven Map<locationId, statusBundle> for a list of
// LocationRoute rows. Only includes rows whose derived status isn't the
// NOT_STARTED default — keeps the lookup small.
export function buildStatusMap(routes, eventBuckets) {
  const map = new Map();
  (routes || []).forEach((stop) => {
    if (!stop?.id) return;
    const bundle = deriveStopStatus({ stop, eventBuckets });
    if (bundle.status !== STATUS_IDS.NOT_STARTED) {
      map.set(stop.id, bundle);
    }
  });
  return map;
}

// Only `NOT_STARTED` rows are still editable. Everything else is locked
// because the driver has touched it in some way.
export function isActionableStatus(statusId) {
  return !statusId || statusId === STATUS_IDS.NOT_STARTED;
}
